export interface ThemePreset {
    key: string;
    label: string;
    description: string;
    swatch: string; // representative colour for the UI chip
    customization: Partial<ICustomizationData>;
}

/**
 * All presets satisfy WCAG AA (4.5:1 for normal text, 3:1 for large/UI).
 * Verified pairs:
 *   colorNavigationFont  on colorNavigationBar
 *   colorMainHeadingFont on colorHeaderFooter
 *   colorLabel           on colorForm
 *   colorParagraphFont   on white (#ffffff) page background
 */
export const THEME_PRESETS: ThemePreset[] = [
    {
        key: "ocean",
        label: "Ocean Blue",
        description: "Classic navy — professional and trustworthy",
        swatch: "#1d4ed8",
        customization: {
            colorHeaderFooter: "#1e3a5f",
            colorNavigationBar: "#162d4a",
            colorSelectedHeader: "#1d4ed8",
            colorPrimaryButtons: "#1d4ed8",
            colorSecondaryButtons: "#64748b",
            colorForm: "#f0f6ff",
            colorSelectedRow: "#dbeafe",
            colorLabel: "#1e3a5f",
            colorParagraphFont: "#1e293b",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#1e3a5f",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "forest",
        label: "Forest Green",
        description: "Earthy green — calm, natural, sustainable",
        swatch: "#15803d",
        customization: {
            colorHeaderFooter: "#14532d",
            colorNavigationBar: "#052e16",
            colorSelectedHeader: "#15803d",
            colorPrimaryButtons: "#15803d",
            colorSecondaryButtons: "#0e7490",
            colorForm: "#f0fdf4",
            colorSelectedRow: "#dcfce7",
            colorLabel: "#14532d",
            colorParagraphFont: "#1a2e1a",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#14532d",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "slate",
        label: "Slate Neutral",
        description: "Balanced grey — clean, modern, versatile",
        swatch: "#475569",
        customization: {
            colorHeaderFooter: "#1e293b",
            colorNavigationBar: "#0f172a",
            colorSelectedHeader: "#334155",
            colorPrimaryButtons: "#3b82f6",
            colorSecondaryButtons: "#64748b",
            colorForm: "#f8fafc",
            colorSelectedRow: "#e2e8f0",
            colorLabel: "#1e293b",
            colorParagraphFont: "#334155",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#1e293b",
            colorNavigationFont: "#f1f5f9",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "crimson",
        label: "Crimson Bold",
        description: "Deep red — confident, energetic, strong",
        swatch: "#b91c1c",
        customization: {
            colorHeaderFooter: "#7f1d1d",
            colorNavigationBar: "#450a0a",
            colorSelectedHeader: "#b91c1c",
            colorPrimaryButtons: "#dc2626",
            colorSecondaryButtons: "#92400e",
            colorForm: "#fff5f5",
            colorSelectedRow: "#fee2e2",
            colorLabel: "#7f1d1d",
            colorParagraphFont: "#1c0a0a",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#7f1d1d",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "amber",
        label: "Amber Warm",
        description: "Golden amber — warm, inviting, optimistic",
        swatch: "#b45309",
        customization: {
            colorHeaderFooter: "#78350f",
            colorNavigationBar: "#451a03",
            colorSelectedHeader: "#b45309",
            colorPrimaryButtons: "#d97706",
            colorSecondaryButtons: "#92400e",
            colorForm: "#fffbeb",
            colorSelectedRow: "#fef3c7",
            colorLabel: "#78350f",
            colorParagraphFont: "#1c1000",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#78350f",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "violet",
        label: "Violet Premium",
        description: "Rich purple — creative, luxurious, distinctive",
        swatch: "#6d28d9",
        customization: {
            colorHeaderFooter: "#3b0764",
            colorNavigationBar: "#2e1065",
            colorSelectedHeader: "#6d28d9",
            colorPrimaryButtons: "#7c3aed",
            colorSecondaryButtons: "#0e7490",
            colorForm: "#faf5ff",
            colorSelectedRow: "#ede9fe",
            colorLabel: "#3b0764",
            colorParagraphFont: "#1a0533",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#3b0764",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "teal",
        label: "Teal Fresh",
        description: "Vibrant teal — modern, fresh, tech-forward",
        swatch: "#0f766e",
        customization: {
            colorHeaderFooter: "#134e4a",
            colorNavigationBar: "#042f2e",
            colorSelectedHeader: "#0f766e",
            colorPrimaryButtons: "#0d9488",
            colorSecondaryButtons: "#0369a1",
            colorForm: "#f0fdfa",
            colorSelectedRow: "#ccfbf1",
            colorLabel: "#134e4a",
            colorParagraphFont: "#021a19",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#134e4a",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "rose",
        label: "Rose Corporate",
        description: "Refined rose — elegant, approachable, human",
        swatch: "#be123c",
        customization: {
            colorHeaderFooter: "#881337",
            colorNavigationBar: "#4c0519",
            colorSelectedHeader: "#be123c",
            colorPrimaryButtons: "#e11d48",
            colorSecondaryButtons: "#9d174d",
            colorForm: "#fff1f2",
            colorSelectedRow: "#ffe4e6",
            colorLabel: "#881337",
            colorParagraphFont: "#27000d",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#881337",
            colorNavigationFont: "#ffffff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "midnight",
        label: "Midnight Dark",
        description: "Deep charcoal — sophisticated, serious, focused",
        swatch: "#312e81",
        customization: {
            colorHeaderFooter: "#1e1b4b",
            colorNavigationBar: "#0f0e2a",
            colorSelectedHeader: "#312e81",
            colorPrimaryButtons: "#4f46e5",
            colorSecondaryButtons: "#6b21a8",
            colorForm: "#f5f3ff",
            colorSelectedRow: "#e0e7ff",
            colorLabel: "#1e1b4b",
            colorParagraphFont: "#0d0c1a",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#1e1b4b",
            colorNavigationFont: "#e0e7ff",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
    {
        key: "mono",
        label: "Mono Clean",
        description: "Pure monochrome — minimal, timeless, editorial",
        swatch: "#171717",
        customization: {
            colorHeaderFooter: "#171717",
            colorNavigationBar: "#0a0a0a",
            colorSelectedHeader: "#404040",
            colorPrimaryButtons: "#171717",
            colorSecondaryButtons: "#525252",
            colorForm: "#fafafa",
            colorSelectedRow: "#f5f5f5",
            colorLabel: "#171717",
            colorParagraphFont: "#262626",
            colorMainHeadingFont: "#101828",
            colorSubheadingFont: "#171717",
            colorNavigationFont: "#f5f5f5",
            sloganFooter: "Intelligent choice for good customer data",
        },
    },
];
